import Api from "../../../core/data/redux/utils/apiCall";

// Section 80 style declarations (S80C / SC80) have two clickable line-item
// columns: "Investments/Contributions" ("Contributions" on SC80) opens a
// read-only help popup, and "Evidence" opens the evidence text.
export const SECTION_80_TYPES = ['S80C', 'SC80'];

// Evidence text is carried on each line row under this key.
export const EVIDENCE_TEXT_KEY = 'C1000T1';

export type LinePopupKind = 'info' | 'evidence';

export const getLinePopupKind = (
  declarationType: string | undefined,
  field: any
): LinePopupKind | null => {
  if (!SECTION_80_TYPES.includes(String(declarationType ?? '').trim().toUpperCase())) return null;
  const label = String(field?.label || field?.CustomerFieldLabel || field?.LineCustomerFieldLabel || '').trim().toLowerCase();
  if (/evidence/i.test(label)) return 'evidence';
  if (/contributions/i.test(label)) return 'info';
  return null;
};

// Hand-written "Detail Help" text for S80C's Contributions popup, keyed by the
// line row's LINUM (stable, unlike the contribution names).
export const S80C_DETAIL_HELP: Record<string, string> = {
  // Payment towards Life Insurance Policy
  '0001': "80C(i) Premium contribution by an employee towards a life insurance policy. In case of an individual, the life insurance policy can be in the name of the employee or employee's spouse or children. In the case of a HUF(Hindu Undivided Family), this can be applicable to any member of the family.",
  // Contribution to Superannuation Fund
  '0002': "80C(vii) Contribution by an employee towards an approved superannuation fund",
  // Subscription to notified Central Government security (NSS)
  '0003': "80C (viii) Contribution to any security or deposit scheme of the Central Government and notified by the Official Gazette",
  // Contribution to ULIP
  '0004': "80C(x) Contribution by an employee towards Unit-linked Insurance Plan, specified in the Unit Trust of India Act, 2002. This contribution should be in the name of the employee, spouse and employee's children.",
  // Subscription to approved equity shares or debentures (Infrastructure)
  '0005': "Sec 80C (xix): 1. subscription to equity shares or debentures forming part of any eligible issue of capital approved by the Board on an application made by a public company. 2. subscription to any eligible issue of capital by any public financial institution. Note: \"Eligible issue of capital\" means an issue made by a public company formed and registered in India or a public financial institution.",
  // Subscription to approved mutual fund (Infrastructure)
  '0006': "80C (xx) Subscription to any units of any mutual fund referred to in clause (23D) of section 10 and approved by the Board on an application made by such mutual fund in the prescribed form: Provided that this clause shall apply if the amount of subscription to such units is subscribed only in the eligible issue of capital of any company.",
  // Contribution to Public Provident Fund
  '0007': "80C(v) Contribution to any Provident Fund set up by the Central Government and Notified by it in the Gazette (i.e. Public Provident Fund, established under the Public Provident Fund Scheme 1968). Contribution can be in the name of the employee, spouse or the employee's children.",
  // Contribution to Recognised Provident Fund
  '0008': "80C(vi) a contribution by an employee to a recognised provident fund;",
  // Subscription to notified Central Govmt Savings certificate (NSC VIII issue)
  '0009': "80C (ix) as a contribution by an employee to any savings certificate of the Central Government (NSC VIII issue) as notified in the Official Gazette;",
  // Contribution to notified unit linked insurance plan of LIC Mutual Fund
  '0010': "80C (xi) Contribution to notified unit linked insurance plan of LIC Mutual Fund",
  // Contribution to provident fund as per PF Act,1925
  '0011': "80C(iv) as a contribution by an individual to any provident fund to which the Provident Funds Act, 1925 (19 of 1925) applies;",
  // Repayment of Housing loan
  '0012': "80C(xviii) Contribution towards the purchase or construction of a residential house property, the income from which is chargeable to tax under the head \"Income from house property\" (or which would, if it had not been used for the assessee's own residence, have been chargeable to tax under that head), where such payments are made towards or by way of repayment of loan;",
  // Interest on NSC VIII issue
  '0013': "Interest on NSC VIII issue deemed to be reinvested and eligible for deduction under Section 80C",
  // Contribution to notified annuity plan of LIC
  '0014': "80C (xii) Contribution to notified annuity plan of LIC (New Jeevan Dhara / New Jeevan Akshay)",
  // Subscription to notified mutual fund
  '0015': "80C (xiii) Subscription to any units of any mutual fund approved by the Central Board of Direct Taxes",
  // Subscription to notified pension fund
  '0016': "80C (xiv) contribution by an individual to any pension fund set up by any Mutual Fund or by the Administrator or the specified company of the Central Government notified by the Official Gazette",
  // Subscription to notified deposit scheme of the National Housing Bank
  '0017': "80C (xv) subscription to any such deposit scheme or a contribution to any pension fund set up by the National Housing Bank established under the National Housing Bank Act, 1987 specified by the Central Government and notified by the Official Gazette.",
  // Subsr to dpst schm of a pblc sectr com or any constituted athry in house constrc
  '0018': "80C (xvi) subscription to any deposit scheme of: (a) a public sector company which is engaged in providing long-term finance for construction or purchase of houses in India for residential purposes or (b) any authority constituted in India by or under any law enacted either for the purpose of dealing with and satisfying the need for housing accommodation or for the purpose of planning, development",
  // Contract for a deferred annuity on life of specified persons
  '0019': "80C (ii) A contract for a deferred annuity, not being an annuity plan such as the Life Insurance Corporation or any other insurer such as the Central Government on the life of the employee, spouse and children of the employee. This contract however does not contain a provision for receiving a cash payment in lieu of the payment of the annuity by the insured.",
  // Deduction from salary for a deferred annuity
  '0020': "80C (iii) Deduction from the salary payable by or on behalf of the Government to any individual for the purpose of securing to him a deferred annuity or making provision for his spouse or children, a sum deducted in accordance with the conditions of his service, such that the sum so deducted does not exceed one-fifth of the salary;",
  // Tuition fee - child 1
  '0021': "80C (xvii) as tuition fees (excluding any payment towards any development fees or donation or payment of similar nature), whether at the time of admission or thereafter: (a) to any university, college, school or other educational institution situated within India; (b) for the purpose of full-time education of any two children of the employee;",
  // Tuition fee - child 2
  '0022': "as tuition fees (excluding any payment towards any development fees or donation or payment of similar nature), whether at the time of admission or thereafter: (a) to any university, college, school or other educational institution situated within India; (b) for the purpose of full-time education of any two children of the employee",
  // Term Deposits under Section 80C
  '0023': "80C (xxi) Contribution towards a term deposit which fulfills the following criteria: (a) Has a fixed period of not less than five years with a scheduled bank (b) Is in accordance with scheme 27a of the Central Government and notified by the Official Gazette",
  // Section 80C - 5 year time deposit under the Post Office Time Deposit Rules 1981
  '0024': "80C (xxiv) 5 year time deposit under the Post Office Time Deposit Rules 1981",
  // Section 80C - Senior Citizens Savings Scheme Rules 2004
  '0025': "80C (xxiii) Contribution in an account under the Senior Citizens Savings Scheme Rules 2004",
  // Section 80C - Sukanya Samriddhi Scheme
  '0026': "Section 80C - Sukanya Samriddhi Scheme: A small savings scheme aimed at encouraging savings for a girl child's education and marriage.",
  // Stamp duty and registration fee
  '0027': "Stamp duty and registration fee: Deduction u/s 80c(2)(xviii)(d) for stamp duty, registration fee and other expenses for the purpose of transfer of house property",
};

export const getDetailHelpText = (
  declarationType: string | undefined,
  lineNumber: string | undefined,
  contributionName: string
): string => {
  const type = String(declarationType ?? '').trim().toUpperCase();
  // SC80 has no help text — opens empty with contribution name
  if (type === 'SC80') return '';
  if (type !== 'S80C') return contributionName;
  return S80C_DETAIL_HELP[String(lineNumber ?? '').trim()] || contributionName;
};

export interface ParsedBackendError {
  message: string;
  bullets: string[];
}

const isGenericNetworkError = (msg: string): boolean => {
  if (!msg || typeof msg !== "string") return false;
  const lower = msg.toLowerCase();
  return (
    lower.includes("request failed with status code") ||
    lower.includes("network error") ||
    lower.includes("an unexpected error occurred")
  );
};

const splitErrorIntoBulletsOrMessage = (
  rawText: string,
  fallbackMessage: string
): ParsedBackendError => {
  const text = String(rawText ?? "").trim();
  if (!text) return { message: fallbackMessage, bullets: [] };

  // 1. Check for newline-delimited errors (e.g. "1. Error A\n2. Error B")
  if (text.includes("\n") || text.includes("\r")) {
    const rawLines = text
      .split(/\r?\n+/)
      .map((l) => l.trim())
      .filter(Boolean);

    if (rawLines.length > 1) {
      const bullets = rawLines.map((l) =>
        l.replace(/^\d+[\.\)]\s*/, "").replace(/^[-•*]\s*/, "").trim()
      );
      return { message: "", bullets };
    } else if (rawLines.length === 1) {
      const clean = rawLines[0].replace(/^\d+[\.\)]\s*/, "").replace(/^[-•*]\s*/, "").trim();
      return { message: clean || rawLines[0], bullets: [] };
    }
  }

  // 2. Check for genuine SAP slash-delimited errors (e.g. "/Error 1/Error 2" or "Error 1 / Error 2")
  // Must NOT split normal text with dates like 01/04/2024 or acronyms like HRA/CLA/COA
  const isSlashDelimited =
    (text.startsWith("/") && text.slice(1).includes("/")) ||
    (text.includes(" / ") && !/\b\d{1,2}\/\d{1,2}\/\d{2,4}\b/.test(text));

  if (isSlashDelimited) {
    const delimiter = text.includes(" / ") ? " / " : "/";
    const parts = text
      .split(delimiter)
      .map((p) => p.trim())
      .filter((p) => p.length > 0 && !/^[\/\\-]+$/.test(p));

    // Ensure it's not date fragments or single short words
    const validParts = parts.filter(
      (p) => !/^\d{1,4}$/.test(p) && !/^(HRA|CLA|COA|CO|NOC|IT)$/i.test(p)
    );

    if (validParts.length > 1) {
      const bullets = validParts.map((l) =>
        l.replace(/^\d+[\.\)]\s*/, "").replace(/^[-•*]\s*/, "").trim()
      );
      return { message: "", bullets };
    } else if (validParts.length === 1) {
      const clean = validParts[0].replace(/^\d+[\.\)]\s*/, "").replace(/^[-•*]\s*/, "").trim();
      return { message: clean || validParts[0], bullets: [] };
    }
  }

  // 3. Check for inline numbered errors without newlines (e.g. "1. Error A 2. Error B")
  if (/1[\.\)]\s+.*\b2[\.\)]\s+/i.test(text)) {
    const parts = text
      .split(/(?=\b\d+[\.\)]\s+)/)
      .map((p) => p.replace(/^\d+[\.\)]\s*/, "").replace(/^[-•*]\s*/, "").trim())
      .filter(Boolean);

    if (parts.length > 1) {
      return { message: "", bullets: parts };
    }
  }

  // Single error message
  const cleanSingle = text.replace(/^\d+[\.\)]\s*/, "").replace(/^[-•*]\s*/, "").trim();
  return { message: cleanSingle || text, bullets: [] };
};

export const parseBackendError = (
  errOrRes: any,
  fallbackMessage: string = "Validation failed. Something went wrong."
): ParsedBackendError => {
  if (!errOrRes) {
    return { message: fallbackMessage, bullets: [] };
  }

  if (typeof errOrRes === "string") {
    return splitErrorIntoBulletsOrMessage(errOrRes, fallbackMessage);
  }

  let current = errOrRes;
  if (current?.response?.data) {
    current = current.response.data;
  }

  const rootData = current?.data;

  // Check for innerError / errors array
  const innerList =
    (Array.isArray(rootData?.innerError) && rootData.innerError) ||
    (Array.isArray(current?.innerError) && current.innerError) ||
    (Array.isArray(rootData?.errors) && rootData.errors) ||
    (Array.isArray(current?.errors) && current.errors) ||
    (Array.isArray(rootData?.data?.innerError) && rootData.data.innerError) ||
    (Array.isArray(current?.data?.errors) && current.data.errors);

  if (innerList && innerList.length > 0) {
    const bullets = innerList
      .map((item: any) => {
        if (typeof item === "string") return item.trim();
        return String(item?.message || item?.description || item?.text || item || "").trim();
      })
      .filter(Boolean);

    if (bullets.length > 0) {
      return { message: "", bullets };
    }
  }

  // Extract raw error string prioritizing backend response body
  let rawText = "";

  if (typeof rootData === "string" && rootData.trim()) {
    rawText = rootData.trim();
  } else if (typeof rootData?.message === "string" && rootData.message.trim() && !isGenericNetworkError(rootData.message)) {
    rawText = rootData.message.trim();
  } else if (typeof rootData?.data?.Message === "string" && rootData.data.Message.trim()) {
    rawText = rootData.data.Message.trim();
  } else if (typeof rootData?.data?.message === "string" && rootData.data.message.trim()) {
    rawText = rootData.data.message.trim();
  } else if (typeof current?.data?.Message === "string" && current.data.Message.trim()) {
    rawText = current.data.Message.trim();
  } else if (typeof current?.data?.message === "string" && current.data.message.trim()) {
    rawText = current.data.message.trim();
  } else if (typeof current?.message === "string" && current.message.trim() && !isGenericNetworkError(current.message)) {
    rawText = current.message.trim();
  } else if (typeof rootData?.Message === "string" && rootData.Message.trim()) {
    rawText = rootData.Message.trim();
  } else if (typeof current?.Message === "string" && current.Message.trim()) {
    rawText = current.Message.trim();
  } else if (typeof current?.message === "string" && current.message.trim()) {
    rawText = current.message.trim();
  }

  if (!rawText) {
    try {
      const apiMsg = Api.getErrorMessage(errOrRes);
      if (apiMsg && typeof apiMsg === "string" && !isGenericNetworkError(apiMsg)) {
        rawText = apiMsg;
      }
    } catch (e) {
      // ignore
    }
  }

  if (!rawText) {
    return { message: fallbackMessage, bullets: [] };
  }

  return splitErrorIntoBulletsOrMessage(rawText, fallbackMessage);
};

export interface StatusConfig {
  color: string;
  label: string;
  bgLight: string;
  border: string;
  icon: string;
}

export const STATUS_UI_MAP: Record<string, { color: string; label: string; icon: string; bgLight: string; border: string }> = {
  N: { color: "#2563EB", label: "New", icon: "ti ti-square-plus", bgLight: "#eff6ff", border: "#bfdbfe" },
  T: { color: "#EA580C", label: "To Be Approved", icon: "ti ti-clock", bgLight: "#fff7ed", border: "#fed7aa" },
  P: { color: "#F59E0B", label: "Partially Approved", icon: "ti ti-clock-pause", bgLight: "#fffbeb", border: "#fde68a" },
  A: { color: "#16A34A", label: "Approved", icon: "ti ti-circle-check", bgLight: "#f0fdf4", border: "#bbf7d0" },
  R: { color: "#DC2626", label: "Rejected", icon: "ti ti-alert-circle", bgLight: "#fef2f2", border: "#fecaca" },
  D: { color: "#7C3AED", label: "Processing Complete", icon: "ti ti-clipboard-check", bgLight: "#faf5ff", border: "#e9d5ff" },
  X: { color: "#64748B", label: "Deleted", icon: "ti ti-trash", bgLight: "#f1f5f9", border: "#cbd5e1" },
  B: { color: "#0891B2", label: "To Be Processed", icon: "ti ti-settings", bgLight: "#ecfeff", border: "#a5f3fc" },
  C: { color: "#334155", label: "Closed", icon: "ti ti-lock", bgLight: "#f8fafc", border: "#cbd5e1" },
  K: { color: "#991B1B", label: "Cancelled", icon: "ti ti-circle-x", bgLight: "#fef2f2", border: "#fecaca" },
  S: { color: "#059669", label: "Settled", icon: "ti ti-check", bgLight: "#ecfdf5", border: "#a7f3d0" },
  I: { color: "#16A34A", label: "Completed", icon: "ti ti-circle-check", bgLight: "#f0fdf4", border: "#bbf7d0" },
  PENDING: { color: "#F59E0B", label: "Pending", icon: "ti ti-clock", bgLight: "#fffbeb", border: "#fde68a" },
  DRAFT: { color: "#64748B", label: "Draft", icon: "ti ti-file", bgLight: "#f1f5f9", border: "#cbd5e1" },
  APPROVED: { color: "#16A34A", label: "Approved", icon: "ti ti-circle-check", bgLight: "#f0fdf4", border: "#bbf7d0" },
  REJECTED: { color: "#DC2626", label: "Rejected", icon: "ti ti-alert-circle", bgLight: "#fef2f2", border: "#fecaca" },
  SAVED: { color: "#2563EB", label: "Saved", icon: "ti ti-device-floppy", bgLight: "#eff6ff", border: "#bfdbfe" },
  INFORMED: { color: "#0891B2", label: "Informed", icon: "ti ti-bell", bgLight: "#ecfeff", border: "#a5f3fc" },
  TO_BE_APPROVED: { color: "#EA580C", label: "To Be Approved", icon: "ti ti-clock", bgLight: "#fff7ed", border: "#fed7aa" },
  PARTIALLY_APPROVED: { color: "#F59E0B", label: "Partially Approved", icon: "ti ti-clock-pause", bgLight: "#fffbeb", border: "#fde68a" },
  TO_BE_PROCESSED: { color: "#0891B2", label: "To Be Processed", icon: "ti ti-settings", bgLight: "#ecfeff", border: "#a5f3fc" },
  PROCESSING_COMPLETE: { color: "#7C3AED", label: "Processing Complete", icon: "ti ti-clipboard-check", bgLight: "#faf5ff", border: "#e9d5ff" },
  APPROVE: { color: "#16A34A", label: "Approved", icon: "ti ti-circle-check", bgLight: "#f0fdf4", border: "#bbf7d0" },
  REJECT: { color: "#DC2626", label: "Rejected", icon: "ti ti-alert-circle", bgLight: "#fef2f2", border: "#fecaca" },
  INPROCESS: { color: "#EA580C", label: "To Be Approved", icon: "ti ti-clock", bgLight: "#fff7ed", border: "#fed7aa" },
  IN_PROCESS: { color: "#EA580C", label: "To Be Approved", icon: "ti ti-clock", bgLight: "#fff7ed", border: "#fed7aa" },
};

export const getStatusConfig = (status?: string): StatusConfig => {
  const raw = String(status || "").trim();
  const rawUpper = raw.toUpperCase().replace(/[\s-]+/g, "_");

  if (STATUS_UI_MAP[rawUpper]) {
    return STATUS_UI_MAP[rawUpper];
  }

  if (rawUpper.includes("PARTIAL")) {
    return STATUS_UI_MAP.PARTIALLY_APPROVED;
  }
  if (
    rawUpper.includes("TO_BE_APPROV") ||
    rawUpper.includes("TO_BE_PROCESSED") ||
    rawUpper === "T" ||
    rawUpper === "IN_PROCESS" ||
    rawUpper === "INPROCESS"
  ) {
    return STATUS_UI_MAP.TO_BE_APPROVED;
  }
  if (rawUpper === "A" || rawUpper === "APPROVED" || rawUpper === "APPROVE") {
    return STATUS_UI_MAP.APPROVED;
  }
  if (rawUpper === "R" || rawUpper.includes("REJECT")) {
    return STATUS_UI_MAP.REJECTED;
  }
  if (rawUpper.includes("PROCESS") || rawUpper.includes("PENDING")) {
    return STATUS_UI_MAP.TO_BE_APPROVED;
  }
  if (rawUpper.includes("DRAFT")) {
    return STATUS_UI_MAP.DRAFT;
  }

  const humanized =
    raw
      .toLowerCase()
      .split(/[_\s]+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ") || "To Be Processed";

  return {
    color: "#64748B",
    label: humanized,
    bgLight: "#f1f5f9",
    border: "#cbd5e1",
    icon: "ti ti-help",
  };
};


